import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  Text,
} from '@fluentui/react';
import { notificationsApi, type AppNotificationDto } from '../../api/notificationsApi';
import { useAuth } from '../../auth';

/**
 * Global daily popup for snoozeable notifications (any route).
 */
export function AppNotificationHost() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState<AppNotificationDto[]>([]);
  const [busy, setBusy] = useState(false);

  const current = queue[0] ?? null;

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    try {
      const items = await notificationsApi.getPopup();
      setQueue(items);
    } catch (err) {
      console.error('Failed to load popup notifications', err);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const dismissCurrent = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await notificationsApi.dismiss(current.id);
      setQueue(prev => prev.slice(1));
      window.dispatchEvent(new CustomEvent('ntforms-notifications-changed'));
    } catch (err) {
      console.error('Failed to dismiss notification', err);
    } finally {
      setBusy(false);
    }
  };

  const openCurrent = async () => {
    if (!current) return;
    const url = current.actionUrl;
    // Dismiss popup for today when they open, but leave in bell until completed
    try {
      await notificationsApi.dismiss(current.id);
      window.dispatchEvent(new CustomEvent('ntforms-notifications-changed'));
    } catch {
      /* ignore */
    }
    setQueue(prev => prev.slice(1));
    if (url) {
      navigate(url);
    }
  };

  if (!current) return null;

  return (
    <Dialog
      hidden={false}
      onDismiss={() => { void dismissCurrent(); }}
      dialogContentProps={{
        type: DialogType.normal,
        title: current.title,
        subText: current.body,
      }}
      modalProps={{ isBlocking: true }}
    >
      {current.severity === 'Urgent' && (
        <Text styles={{ root: { color: '#a4262c', fontWeight: 600, display: 'block', marginBottom: 8 } }}>
          Past due
        </Text>
      )}
      <DialogFooter>
        <PrimaryButton
          text={current.actionLabel || 'Open'}
          onClick={() => { void openCurrent(); }}
          disabled={busy}
        />
        <DefaultButton
          text="Dismiss for today"
          onClick={() => { void dismissCurrent(); }}
          disabled={busy}
        />
      </DialogFooter>
    </Dialog>
  );
}
